import { ReactNode } from 'react';
import { Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import dayjs from 'dayjs';
import { DATE_FORMAT, FULL_DATE_FORMAT } from 'constants/dates';
import {
  INVOICE_DOCUMENT_TYPE_LABELS,
  INVOICE_PAYMENT_METHOD_LABELS,
  INVOICE_STATUS_COLORS,
  INVOICE_STATUS_LABELS
} from 'constants/invoicing';
import {
  Invoice,
  InvoiceDocumentType,
  InvoicePaymentMethod,
  InvoiceStatus
} from 'types/api/invoicing';

import { money } from 'utils/price';

/**
 * The number cell. Invoices and credit notes share ONE number sequence, so
 * the number alone does not say which document a row is — the tag does.
 *
 * Exported because the patient history tab wraps the number in a Link; it
 * passes its own content and still gets the tag.
 */
export const documentCell = (row: Invoice, content: ReactNode) => (
  <>
    {content}
    {row.documentType === InvoiceDocumentType.CreditNote && (
      <Tag color="purple" style={{ marginInlineStart: 8 }}>
        {INVOICE_DOCUMENT_TYPE_LABELS[InvoiceDocumentType.CreditNote]}
      </Tag>
    )}
  </>
);

/**
 * Column definitions shared by the invoices list and the patient invoice
 * history tab. They carry presentation only — no sorters, no row actions —
 * so each table can compose what it needs:
 *
 *   { ...INVOICE_COLUMNS.status, ...sortCol('status') }
 */
export const INVOICE_COLUMNS: Record<
  'invoiceNumber' | 'status' | 'paymentMethod' | 'total' | 'due' | 'created',
  ColumnProps<Invoice>
> = {
  invoiceNumber: {
    title: 'Document',
    dataIndex: 'invoiceNumber',
    key: 'invoiceNumber',
    render: (value: string | null | undefined, row: Invoice) =>
      documentCell(row, value || '—')
  },
  status: {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (status: InvoiceStatus) => (
      <Tag color={INVOICE_STATUS_COLORS[status]}>
        {INVOICE_STATUS_LABELS[status] ?? status}
      </Tag>
    )
  },
  paymentMethod: {
    title: 'Payment',
    dataIndex: 'paymentMethod',
    key: 'paymentMethod',
    render: (method: InvoicePaymentMethod) =>
      INVOICE_PAYMENT_METHOD_LABELS[method] ?? method
  },
  total: {
    title: 'Total',
    dataIndex: 'totalAmount',
    key: 'totalAmount',
    align: 'right',
    // A credit note is negative. Colour it so a mixed list does not read as
    // if the amounts simply add up.
    render: (value: string, row) => (
      <span style={{ color: Number(value) < 0 ? '#cf1322' : undefined }}>
        {money(value, { currency: row.currency })}
      </span>
    )
  },
  due: {
    title: 'Due',
    dataIndex: 'dueDate',
    key: 'dueDate',
    render: (value?: string | null) =>
      value ? dayjs(value).format(DATE_FORMAT) : '—'
  },
  created: {
    title: 'Created',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: (value: string) => dayjs(value).format(FULL_DATE_FORMAT)
  }
};
